import { clamp } from '@/core/rng';
import { DAY } from '@/sim/clock';
import type { EventBus, EventType } from '@/sim/events';
import type { SimSystem } from '@/sim/scheduler';
import type { HabitatSampler, SocialHabitat } from '@/sim/social/habitat';
import {
  SOCIAL_CELL_SIZE, socialCenter, socialKey, type SocialCellKey,
} from '@/sim/social/socialGrid';
import type { WildlifeSnapshot } from '@/sim/persistence/snapshot';
import type { SereStore, SereStage, StageShares } from '@/world/sere';
import type { StrainReads } from '@/world/strainReads';
import type { StrainTraits } from '@/world/strain';
import { seasonalDryness } from '@/world/weather';
import { MIN_HERD, inclinationOf, migrationPass } from './migration';
import {
  HERBIVORES, WILDLIFE_ARCHETYPES, WILDLIFE_SPECIES, zeroCounts,
  type BeastProcess, type CapacityTerms, type CellView, type HerdPress, type HerdPresses,
  type Stirring, type WildlifeCellRecord,
  type WildlifeCellState, type WildlifePopulation, type WildlifeSpeciesId,
} from './types';

/**
 * Statistical wildlife on the social grid.
 *
 * Every stocked cell holds a head count per species. Once per world-day —
 * on the absolute daily grid, never "since last call" — each count moves
 * logistically toward a carrying capacity recomputed from that day's habitat
 * reading: base species from forage, wood and water; predators from the prey
 * standing beside them; scavengers from the carrion the others leave behind.
 * Then the herds that lost their K walk out (see migration.ts).
 *
 * No dice anywhere: growth, predation and harvest are plain arithmetic, and
 * the one drawn number (migration's moved fraction) hashes from stable
 * identifiers. Identical state and identical spans give identical herds,
 * however the span is chopped.
 *
 * WHAT MAKES K MOVE (ROADMAP §42). Measured before any of it was written
 * (`tools/trajectory-drift.ts`): over ten god-free years a cell's K moved
 * exactly 0.000 year over year for every base species, island totals were
 * flat to four figures, and a burned cell's herds behaved identically to an
 * unburned one's — the habitat sampler reads the forty-day char and never the
 * eighty-year sere. The world was a photograph that aged. Three things now
 * move the herbivore K, each read from a field the world already keeps:
 *
 *   - the SERE's stage shares — a burn's grass years carry more grazing and
 *     no browse, its shrub and young years bring the browsers back, and at
 *     eighty years the cell is climax again: fire is a decades-long wildlife
 *     story (Design III §54, §60);
 *   - the SEASON — `weather.ts`'s dryness curve, the same one the fire reads,
 *     cures the forage on ground with no water in late summer, so herds drift
 *     to water and back (III §55's seasonal range);
 *   - the cell's RANGE CONDITION — the one new state, a slow variable that
 *     overstock strips in days and years rebuild, so a boom (a lapsed
 *     blessing, a fire crash) leaves a mark the K reads long after (law 19,
 *     III §65). At equilibrium nothing is overstocked and the range is 1, so
 *     the tuned world of the census is exactly the world it was.
 */

/** When K collapses under a herd, this share of the excess dies per day. */
const EXCESS_MORTALITY = 0.25;
/** Prey head that feed one predator. Exported for the Beast read model, which
 *  says *hungrier than the ground can feed* off the same number. */
export const PREDATOR_SUPPORT = 30;
/** Head of prey one predator removes per day. */
export const PREDATOR_INTAKE = 0.12;
/** Predators never strip more than this share of a prey population per day. */
export const MAX_PREDATION = 0.08;
/** Carrion rots on a 4-day half-life. */
const CARRION_KEEP = Math.pow(0.5, 1 / 4);
/** Share of a predator kill left on the ground for the scavengers. */
const CARRION_PER_KILL = 0.5;
/** Scavenger head one head-unit of carrion supports. */
export const SCAV_PER_CARRION = 2.5;
/** Scavenger head a fully disturbed cell attracts on unrest alone. */
export const SCAV_INSTABILITY = 15;
/** Share of the herd a full-effort, fully-huntable harvest takes in one call. */
const HARVEST_TAKE = 0.4;
/** Share of the harvest left as offal for the scavengers. */
const HARVEST_OFFAL = 0.25;
/** Below this a population is written off as locally extinct. */
const EXTINCT = 0.05;

/**
 * What each recovery stage does to the FORAGE a cell offers, as a multiplier
 * on the climax reading — the burn's flush of grass is better grazing than
 * closed wood, a young stand shades it out again — and to its BROWSE, which
 * is gone until shrubs stand. Climax (no cell) is 1. These are design
 * constants shaped to `sere.ts`'s own ladder (STAGE_FUEL bare 0.05 → young
 * 0.75, STAGE_CEILING 0 → 14 m), not measurements; what IS measured is their
 * effect — `tools/trajectory-burn.ts` follows one burn's herds for its whole
 * recovery, and that is the number to re-take if these move.
 */
export const STAGE_FORAGE: Record<SereStage, number> = { bare: 0.10, grass: 1.20, shrub: 0.85, young: 0.80 };
export const STAGE_BROWSE: Record<SereStage, number> = { bare: 0.00, grass: 0.00, shrub: 0.35, young: 0.75 };

/**
 * How much of a waterless cell's forage the late-summer cure takes, at the
 * peak of `seasonalDryness`. Scaled by (1 − waterAccess), so a river or coast
 * cell keeps its grass green — which is why herds walk to water. Priced by
 * `tools/trajectory-season.ts` (see ROADMAP §42): under 0.4 the cut is
 * absorbed by mortality in place; the herds move only when it is deep enough
 * to make the river cell read BETTER (migration.ts) than home.
 */
export const DRY_CUT = 0.5;

/** A stripped range still carries this share of the habitat's K. */
export const RANGE_MIN = 0.35;
/** Range recovers toward 1 with this e-folding time (world-years). */
export const RANGE_RECOVER_YEARS = 2.5;
const RANGE_RECOVER = 1 / (RANGE_RECOVER_YEARS * 365);
/**
 * Range stripped per day per UNFED head — the head standing above what the
 * habitat's own K0 can feed, as a fraction of the most of that kind the cell
 * could ever stand (`headAtBest × landFraction`), summed over the herbivores.
 * Consumption is the herd, regrowth is the K, and the difference is what
 * wears the ground; a herd at or under its K wears nothing, whatever the K
 * is doing, which is what keeps a chronic few percent of overstock (the
 * season easing K down under a herd that tracks it in days) at nothing.
 *
 * Sized against the excess-mortality curve: excess sheds on a 2.4-day
 * half-life, so ∫excess dt = 3.46 × the first day's excess. A 30-head grazer
 * cell left at twice its K by a lapsed blessing: 30 / 80 = 0.375 a day,
 * capped and decaying ≈ 1.2 head-days per headAtBest, × 0.15 ≈ 0.18 of
 * range; the fire in `tools/trajectory-burn.ts` ≈ 0.28. Both heal on
 * `RANGE_RECOVER_YEARS`. Written as a fraction of pop/K0 instead this read a
 * fire as 900% overstock and zeroed the range in a day, which was the fire's
 * doing and not the herd's.
 */
export const RANGE_STRIP = 0.15;
/**
 * The most unfed-head fraction one day may count — a herd more than a third
 * over what its ground could ever stand is a fire's or a Weaken's doing, and
 * the days it takes to die back are counted, not the height of the spike.
 * With the max over the herbivores rather than their sum (a whole-cell burn
 * unfeeds all three at once): the fire case in `tools/trajectory-burn.ts`
 * costs ~0.28 of range and heals inside three years, the lapsed-blessing case
 * ~0.18. Summed and uncapped, the same fire zeroed the range in four days.
 */
const UNFED_CAP = 0.35;
/**
 * …and the least that counts at all. The season eases a dry cell's K down
 * over a hundred days and the herd tracks it on a 2.4-day lag, so a percent
 * or two of the herd is always "unfed" on the way down: 0.2–0.85% of what
 * the kind could stand, under this floor, so a world at rest keeps its range
 * at exactly 1 (asserted in `test/trajectory.ts`). A lapsed blessing is
 * 0.375 the first day and a whole-cell burn 0.84 — the floor is nothing to them.
 */
const UNFED_FLOOR = 0.02;

/** A species is GONE from a cell once fewer than this many head stand… */
const VANISH_HEAD = 1;
/** …and BACK once this many do — the same bar migration.ts uses for a herd. */
const RETURN_HEAD = 2;
/**
 * A return is worth SAYING only after this long away (world-seconds); sooner
 * is a flicker. Half a year: a season gone is an absence, a fortnight is not.
 * (A whole year missed the first real case by thirteen days — a burn's
 * browsers fall under a head a fortnight after the fire and are back on the
 * first day of grass, 352 days later.)
 */
const RETURN_AFTER = 182 * DAY;
/**
 * The two natural-history kinds this system emits (ROADMAP §42). They were
 * cast through `EventType` while `sim/events.ts` — the answer-back lane's file
 * — did not yet name them; the union carries both since integration
 * (2026-08-15) and the casts are gone. Nothing else about the record is
 * special — cause is natural, nothing in it names a hand (law 10).
 */
const VANISHED: EventType = 'wildlife_vanished';
const RETURNED: EventType = 'wildlife_returned';
/** How out of balance a cell has to be before `stirring` names it. */
const STIR_MIN = 0.25;
/**
 * What a herd of each kind is worth noticing, for `stirring`'s ranking — the
 * per-kind asymmetry §37 accepted for greatMove, applied to attention. On the
 * shipped century the cells bands hunt hardest sit at 12–44% of their small
 * game K (tools/vacancy.ts p10 0.44) and regrow at 8% a day, so unweighted
 * the list was 474 of 480 entries small game (`tools/trajectory-stir.ts`);
 * true, and a rabbit is three pixels at 170 m where a grazer is at 678 m
 * (ROADMAP §38). A god notices the herds first.
 */
const NOTICE: Record<WildlifeSpeciesId, number> =
  { grazer: 1, browser: 1, small_game: 0.35, fish: 0, predator: 0.9, scavenger: 0.5 };

/**
 * ── A HAND ON THE HERD — the Beasts direction's ACT ────────────────────────
 *
 * `press` on a cell is a signed lean on what a herd of one kind is doing:
 * positive reinforces it, negative diminishes it. It is written by
 * `WildlifeSystem.lean` (from `Simulation.act`, for a god whose Godhood lets
 * Strengthen mean the herd's own drive), decays here on a measured half-life,
 * and is read by exactly three processes, each only while it is happening:
 * the migration pass (the bar and the moved fraction), the logistic growth
 * term (while the herd is under its K), and the predation intake (while there
 * is prey). Law 21 — reinforce a process that is happening, scaled by how much
 * it is — is therefore structural: a press on a herd that is not inclined to
 * move, not gathering and not hunting is refused at the write and would do
 * nothing at the read.
 *
 * ONE press PER PROCESS, and that was measured rather than chosen. The first
 * cut kept one number per herd — *the hand is on its vigor to do what it is
 * doing* — and `test/beasts.ts` §5 found the hole in a day: a herd pressed to
 * fill its thinned ground filled it, its emptied neighbours then read as
 * better, and the same press lowered its bar and walked it back out. A lean
 * on increase had become a lean on leaving. So the ledger names the process
 * (law 13), and each reader takes only its own.
 *
 * `PRESS_PER_CAST` — what one channelled cast at the ceiling concentration
 * deposits: half of a whole press, so the second cast on the same herd
 * saturates it and a third does nothing (law 25 by construction, the same
 * shape as `world/things.ts`). `PRESS_HALFLIFE` — six world-days: at 192× the
 * clock that is forty-five real minutes, so a session's cast plays out inside
 * the session and is gone by the next; a god who wants it to last buys *Long
 * Watch*, which lengthens the half-life at the deposit. Both are design
 * numbers written down to be measured against; `test/beasts.ts` pins the
 * shape, and `tools/press-herd.ts` is where the tuning probe would go.
 */
export const PRESS_PER_CAST = 0.5;
export const PRESS_HALFLIFE = 6;
/** The most a press can lean, either way. */
export const PRESS_MAX = 1;
/** Below this magnitude a press is gone from the ledger. */
const PRESS_FLOOR = 0.01;

/** How much a press moves the two terms it reads — the same clamp both ways. */
export const pressFactor = (u: number): number => clamp(1 + clamp(u, -PRESS_MAX, PRESS_MAX), 0.25, 2);

/** A press record, two levels deeper than a spread. */
const PROCESSES: readonly BeastProcess[] = ['movement', 'increase', 'hunt'];
const copyPress = (p: HerdPresses): HerdPresses => {
  const out: HerdPresses = {};
  for (const s of WILDLIFE_SPECIES) {
    const by = p[s];
    if (!by) continue;
    const o: Partial<Record<BeastProcess, HerdPress>> = {};
    for (const k of PROCESSES) if (by[k]) o[k] = { ...by[k]! };
    if (Object.keys(o).length) out[s] = o;
  }
  return out;
};

/** The world-second of the day boundary containing `t` — habitat's own convention. */
const dayStart = (t: number) => Math.floor(t / DAY) * DAY;

export class WildlifeSystem implements SimSystem {
  private readonly cells = new Map<SocialCellKey, WildlifeCellState>();

  /**
   * What burned ground remembers (`world/sere.ts`), read for its stage
   * shares. Absent, every cell reads climax and the K is the habitat's alone
   * — exactly the pre-§42 world. Handed in by `useSere` after construction
   * (the Simulation builds its systems in an order this lane does not own),
   * so a world without the wiring line is unchanged rather than broken.
   */
  private sere?: SereStore;

  constructor(
    private readonly seed: number,
    private readonly habitat: HabitatSampler,
    private readonly events: EventBus,
  ) {}

  /** Let the herds read the sere. Idempotent; the next daily step reads it. */
  useSere(sere: SereStore): void {
    this.sere = sere;
  }

  /**
   * Let the herds read the strains (2026-08-19, the wake). Idempotent, the
   * `useSere` pattern exactly, so `fromSnapshot` — which constructs and then
   * restores — is covered. What an animal strain moves, per its axis contract
   * in `strain.ts`: `yield` scales the food a head yields and the head it
   * contributes to a predator's prey sum; `tolerance` divides the die-down's
   * excess mortality; `exposure` weights its share of the predation split.
   * All read at the CELL's own centre — herd truth is per cell, so its strain
   * is too — and all exactly 1 wherever the ground is ordinary.
   */
  useStrains(reads: StrainReads): void {
    this.strains = reads;
  }
  private strains: StrainReads | null = null;
  private readonly straits: StrainTraits = { yield: 1, tolerance: 1, exposure: 1, spread: 1 };

  /** The effective traits of one species on one cell, all-ones without reads. */
  private traitsOf(s: WildlifeSpeciesId, cell: WildlifeCellState, now: number): StrainTraits {
    const out = this.straits;
    if (!this.strains || (s !== 'grazer' && s !== 'browser' && s !== 'small_game' && s !== 'predator')) {
      out.yield = 1; out.tolerance = 1; out.exposure = 1; out.spread = 1;
      return out;
    }
    this.strains.hostTraitsAtCell(s, cell.sx, cell.sz, now, out);
    return out;
  }

  /**
   * Stock every land social cell in the box from the habitat. Call once at
   * world start. Populations open at capacity — the land begins full, and the
   * simulation's first days settle the food chain, not an empty map. Cells
   * already stocked are left alone, so overlapping boxes cannot restock.
   */
  seedPopulations(box: { sx0: number; sz0: number; sx1: number; sz1: number }, now: number): void {
    for (let sz = box.sz0; sz <= box.sz1; sz++) for (let sx = box.sx0; sx <= box.sx1; sx++) {
      const key = socialKey(sx, sz);
      if (this.cells.has(key)) continue;
      const h = this.habitat.at(sx, sz, now);
      if (h.landFraction <= 0) continue;
      const cell: WildlifeCellState = {
        sx, sz, pop: zeroCounts(), cap: zeroCounts(), prevCap: zeroCounts(),
        carrion: 0, range: 1, gone: {}, updatedAt: now,
      };
      const shares = this.sharesFor(cell, now);
      // species order matters: prey stand before the predator's K reads them
      for (const s of WILDLIFE_SPECIES) {
        const k = this.capacity(s, h, cell, shares, now);
        cell.cap[s] = k;
        cell.prevCap[s] = k;
        cell.pop[s] = k;
      }
      // scavengers open on a toehold: their K is carrion, and nothing has died
      // yet — without a seed population the carrion economy could never start
      cell.pop.scavenger = Math.max(cell.pop.scavenger,
        (cell.pop.grazer + cell.pop.browser + cell.pop.small_game) / 80);
      this.cells.set(key, cell);
    }
  }

  /**
   * Advance on the absolute daily grid: every boundary k·DAY in (from, to]
   * runs one daily step. Same construction as the scheduler — multiply, never
   * accumulate — so advance(a,b) then advance(b,c) is exactly advance(a,c).
   */
  advance(from: number, to: number): void {
    if (!(to > from) || this.cells.size === 0) return;
    let k = Math.floor(from / DAY) + 1;
    for (let t = k * DAY; t <= to; t = ++k * DAY) this.daily(t, k);
  }

  /** The head count standing on a cell, zero where unstocked. */
  populationAt(sx: number, sz: number, species: WildlifeSpeciesId): number {
    return this.cells.get(socialKey(sx, sz))?.pop[species] ?? 0;
  }

  /**
   * One cell's whole ledger, read-only, for the Beast read model
   * (`reading.ts`) — which needs the neighbours' K and prevCap, the carrion,
   * the range and what is gone, all at once, and would otherwise re-derive
   * them from six separate getters. Undefined where unstocked. The
   * `Readonly` is the contract: `reading.ts` is a reader (law 9) and
   * `test/beasts.ts` advances a read world against an unread one.
   */
  cellRead(sx: number, sz: number): Readonly<WildlifeCellState> | undefined {
    return this.cells.get(socialKey(sx, sz));
  }

  /** Every stocked cell, read-only, keyed as the ledger keys them. */
  cellsRead(): ReadonlyMap<SocialCellKey, Readonly<WildlifeCellState>> {
    return this.cells;
  }

  /**
   * WHY the ground carries what it does — the terms `capacity` actually
   * multiplies, handed out so a card can say *the burn's grass years are
   * carrying them* or *the range is worn under them* off the same numbers the
   * herds live by, and never off a guess. Pure read: nothing here is written,
   * and the habitat sampler is a cache over a pure function of position.
   */
  capacityTerms(sx: number, sz: number, species: WildlifeSpeciesId, now: number): CapacityTerms | null {
    const cell = this.cells.get(socialKey(sx, sz));
    if (!cell) return null;
    const h = this.habitat.at(sx, sz, now);
    const shares = this.sharesFor(cell, now);
    const a = WILDLIFE_ARCHETYPES[species];
    let prey = 0;
    for (const p of a.prey) prey += cell.pop[p];
    // the season's cure on the forage, exactly as `capacity` applies it
    const cure = a.aquatic ? 0
      : DRY_CUT * seasonalDryness(dayStart(now)) * (1 - h.waterAccess);
    return {
      forage: h.foragePotential, woody: h.woodyBiomass, water: h.waterAccess,
      landFraction: h.landFraction, instability: h.instability,
      shares, cure,
      range: cell.range,
      rangeFactor: HERBIVORES.includes(species) ? RANGE_MIN + (1 - RANGE_MIN) * cell.range : 1,
      prey, carrion: cell.carrion,
      cap: cell.cap[species], prevCap: cell.prevCap[species],
    };
  }

  /** Range condition under a cell, 1 where pristine or unstocked. */
  rangeAt(sx: number, sz: number): number {
    return this.cells.get(socialKey(sx, sz))?.range ?? 1;
  }

  /**
   * One species' standing in one cell, for a card to phrase — head, what
   * the ground carries, the range, and which way it is going. `trend` reads
   * the gap between the two: a herd well under its K is growing (or being
   * joined), one over it is dying back or leaving. Pure read.
   */
  cellView(sx: number, sz: number, species: WildlifeSpeciesId): CellView | null {
    const c = this.cells.get(socialKey(sx, sz));
    if (!c) return null;
    const head = c.pop[species], cap = c.cap[species];
    const trend = head < cap * 0.9 && cap > 0.5 ? 'growing'
      : head > cap * 1.1 && head > 0.5 ? 'declining' : 'steady';
    const since = c.gone[species];
    return { head, cap, range: c.range, trend, ...(since !== undefined ? { absentSince: since } : {}) };
  }

  /**
   * Where something is going somewhere right now — every (cell, species)
   * visibly out of equilibrium, strongest first. Stateless: computed from
   * today's ledger, writes nothing (law 9), so it answers at boot on a file
   * that has never been advanced. Strength is the imbalance weighted by how
   * big a herd it concerns, so a burn that will carry a great herd outranks
   * two rabbits short of their K.
   */
  stirring(limit = 32): Stirring[] {
    const out: Stirring[] = [];
    for (const c of this.cells.values()) {
      const centre = socialCenter(c.sx, c.sz);
      let bestHerb: WildlifeSpeciesId = 'grazer';
      for (const s of WILDLIFE_SPECIES) {
        if (s === 'fish') continue;
        const pop = c.pop[s], cap = c.cap[s];
        const scale = Math.max(pop, cap);
        if (scale <= 0.5) continue;
        if (HERBIVORES.includes(s) && pop > c.pop[bestHerb]) bestHerb = s;
        const weight = clamp(scale / (2 * WILDLIFE_ARCHETYPES[s].greatMove), 0, 1) * NOTICE[s];
        if (pop < cap) {
          const gap = (cap - pop) / cap;
          if (gap >= STIR_MIN) out.push({ sx: c.sx, sz: c.sz, x: centre.x, z: centre.z, species: s, kind: 'gathering', strength: gap * weight, head: pop });
        } else if (pop > cap) {
          const gap = (pop - cap) / pop;
          if (gap >= STIR_MIN) out.push({ sx: c.sx, sz: c.sz, x: centre.x, z: centre.z, species: s, kind: 'thinning', strength: gap * weight, head: pop });
        }
      }
      if (c.range < 1 - STIR_MIN) {
        out.push({ sx: c.sx, sz: c.sz, x: centre.x, z: centre.z, species: bestHerb, kind: 'stripped', strength: 1 - c.range, head: c.pop[bestHerb] });
      }
    }
    // strongest first; ties by position so the order is a function of the world
    out.sort((a, b) => b.strength - a.strength || a.sx - b.sx || a.sz - b.sz || a.species.localeCompare(b.species));
    return out.slice(0, limit);
  }

  /**
   * Hunt: deterministic yield in food units; reduces the real population.
   * effort01 0..1. Plain arithmetic — same herd, same effort, same take —
   * so a hunting party can never reroll its luck by asking again... though
   * asking again hunts an already-thinned herd.
   */
  harvest(sx: number, sz: number, species: WildlifeSpeciesId, effort01: number, now: number): number {
    const cell = this.cells.get(socialKey(sx, sz));
    if (!cell) return 0;
    const a = WILDLIFE_ARCHETYPES[species];
    const pop = cell.pop[species];
    const take = pop * a.huntability * clamp(effort01, 0, 1) * HARVEST_TAKE;
    if (take <= 0) return 0;
    const left = pop - take;
    cell.pop[species] = left < EXTINCT ? 0 : left;
    // field dressing leaves offal on the ground — the scavengers follow the hunt
    cell.carrion += take * HARVEST_OFFAL;
    cell.updatedAt = now;
    // what a head is worth rides its strain's yield: hunting a rich strain's
    // herd feeds a band better, which is the first thing a mortal economy can
    // notice about a variety without any panel saying so (law 12)
    return take * a.foodPerHead * this.traitsOf(species, cell, now).yield;
  }

  /**
   * A HAND ON WHAT A HERD IS DOING — the write behind the Beasts direction's
   * one ACT. `amount` is signed and in whole presses (`PRESS_PER_CAST` for one
   * channelled cast); `halfDays` is how long it lasts; `want` is what the
   * god's Godhood lets Strengthen mean here. The herd's own state decides
   * which of those it is actually doing, and how much:
   *
   *  · movement — inclined to leave (a better neighbour, or ground that has
   *    failed under them), scaled by how inclined (`Inclination.gradient`);
   *  · increase — gathering onto ground with room for more of them, scaled by
   *    how much room;
   *  · hunt — a hunter with prey standing on its ground.
   *
   * The strongest applicable one carries the press and is reported. Nothing
   * applicable — a settled herd, an empty cell, a hunter with nothing to hunt
   * — writes nothing and says so (`process: null`), which is `touched: 0` for
   * the wild: a fact the god needs, never a failure to hide.
   *
   * No god is recorded here (law 10): the ledger says something leans on this
   * herd and never whose hand. Two hands on one herd blend — amounts sum
   * inside `PRESS_MAX`, and the half-life is the amount-weighted mean of the
   * two, so a patient god's lean is not shortened by an impatient one's.
   */
  lean(
    sx: number, sz: number, species: WildlifeSpeciesId, amount: number, halfDays: number,
    want: { movement: boolean; increase: boolean; hunt: boolean },
  ): { process: 'movement' | 'increase' | 'hunt' | null; delivered: number } {
    const cell = this.cells.get(socialKey(sx, sz));
    if (!cell || !(amount !== 0) || !Number.isFinite(amount)) return { process: null, delivered: 0 };
    const head = cell.pop[species], cap = cell.cap[species];
    const a = WILDLIFE_ARCHETYPES[species];
    let process: 'movement' | 'increase' | 'hunt' | null = null;
    let viability = 0;
    if (a.prey.length) {
      let prey = 0;
      for (const p of a.prey) prey += cell.pop[p];
      if (want.hunt && species === 'predator' && head >= 1 && prey > 0) { process = 'hunt'; viability = 1; }
    } else {
      if (want.movement && head >= MIN_HERD) {
        const inc = inclinationOf(this.cells, cell, species);
        const v = inc.declined ? 1 : clamp(inc.gradient, 0, 1);
        if (v > 0) { process = 'movement'; viability = v; }
      }
      if (want.increase && head >= 1 && cap > 0.5 && head < cap * 0.9) {
        // how much room: the gap as a share of K, full at half empty
        const v = clamp(((cap - head) / cap) / 0.5, 0, 1);
        if (v > viability) { process = 'increase'; viability = v; }
      }
    }
    if (!process) return { process: null, delivered: 0 };
    const delivered = amount * viability;
    const prev = cell.press?.[species]?.[process];
    const v0 = prev?.v ?? 0, h0 = prev?.half ?? halfDays;
    const v1 = clamp(v0 + delivered, -PRESS_MAX, PRESS_MAX);
    const w0 = Math.abs(v0), w1 = Math.abs(delivered);
    const half = w0 + w1 > 0 ? (h0 * w0 + halfDays * w1) / (w0 + w1) : halfDays;
    const by = (cell.press ??= {})[species] ??= {};
    if (Math.abs(v1) < PRESS_FLOOR) delete by[process];
    else by[process] = { v: v1, half };
    if (Object.keys(by).length === 0) delete cell.press[species];
    if (Object.keys(cell.press).length === 0) delete cell.press;
    return { process, delivered };
  }

  /**
   * What is leaning on a herd right now, signed; 0 for nothing. Pure read.
   * With a process, that process's press; without, the strongest of any.
   */
  pressOn(sx: number, sz: number, species: WildlifeSpeciesId, process?: BeastProcess): number {
    const by = this.cells.get(socialKey(sx, sz))?.press?.[species];
    if (!by) return 0;
    if (process) return by[process]?.v ?? 0;
    let best = 0;
    for (const k of PROCESSES) { const v = by[k]?.v ?? 0; if (Math.abs(v) > Math.abs(best)) best = v; }
    return best;
  }

  /** World-wide head counts, one number per species. */
  totals(): Record<WildlifeSpeciesId, number> {
    const out = zeroCounts();
    for (const c of this.cells.values()) {
      for (const s of WILDLIFE_SPECIES) out[s] += c.pop[s];
    }
    return out;
  }

  /** Every (cell, species) reading, in canonical cell-then-species order. */
  populations(): WildlifePopulation[] {
    const out: WildlifePopulation[] = [];
    for (const c of this.cells.values()) {
      for (const s of WILDLIFE_SPECIES) {
        out.push({
          species: s, sx: c.sx, sz: c.sz,
          population: c.pop[s], carryingCapacity: c.cap[s], updatedAt: c.updatedAt,
        });
      }
    }
    return out;
  }

  /**
   * Every stocked cell, copied out in insertion order — the daily loop and
   * the migration pass walk that order, so it is replay state. prevCap and
   * carrion ride along: the decline trigger and the scavenger's K read them.
   * `range` and `gone` are the two §42 fields; `gone` is written only when it
   * holds something, so an untouched cell's record is byte-for-byte what it
   * was plus one number.
   */
  toSnapshot(): WildlifeSnapshot {
    const cells: WildlifeCellRecord[] = [...this.cells.values()].map((c) => ({
      sx: c.sx, sz: c.sz,
      pop: { ...c.pop }, cap: { ...c.cap }, prevCap: { ...c.prevCap },
      carrion: c.carrion, updatedAt: c.updatedAt,
      range: c.range,
      ...(Object.keys(c.gone).length ? { gone: { ...c.gone } } : {}),
      // the press is a record of small objects, so it is copied one level
      // deeper than a spread — a shared `{v, half}` is exactly the silent
      // corruption this repository has been bitten by twice
      ...(c.press ? { press: copyPress(c.press) } : {}),
    }));
    return { cells };
  }

  /** Replace all cell state in place. Shares nothing with the source. */
  restore(snap: WildlifeSnapshot): void {
    this.cells.clear();
    for (const raw of snap.cells) {
      const c = raw as WildlifeCellRecord;
      this.cells.set(socialKey(c.sx, c.sz), {
        sx: c.sx, sz: c.sz,
        pop: { ...c.pop }, cap: { ...c.cap }, prevCap: { ...c.prevCap },
        carrion: c.carrion,
        // a record written before the field: pristine range, nothing known gone
        range: c.range ?? 1, gone: c.gone ? { ...c.gone } : {},
        // …and nothing leaning on any herd
        ...(c.press ? { press: copyPress(c.press) } : {}),
        updatedAt: c.updatedAt,
      });
    }
  }

  /** The cell's stage shares as of the day containing `t` — climax if there is no sere to ask. */
  private sharesFor(cell: WildlifeCellState, t: number): StageShares {
    if (!this.sere) return { bare: 0, grass: 0, shrub: 0, young: 0 };
    return this.sere.stageSharesAt(
      (cell.sx + 0.5) * SOCIAL_CELL_SIZE, (cell.sz + 0.5) * SOCIAL_CELL_SIZE, SOCIAL_CELL_SIZE, t);
  }

  /**
   * What the cell can carry — the species-weighted habitat, or the food chain.
   * For the range-eating species the habitat reading is first passed through
   * the sere's stage shares and the season; the range condition is applied by
   * the caller (`daily`), which needs the un-ranged K0 to measure overstock.
   */
  private capacity(s: WildlifeSpeciesId, h: SocialHabitat, cell: WildlifeCellState,
                   shares: StageShares, t: number): number {
    const a = WILDLIFE_ARCHETYPES[s];
    if (s === 'predator') {
      // predators are carried by prey, not by grass — and by what a head of
      // prey is WORTH: a rich strain's herd feeds more hunting than a poor one
      let prey = 0;
      for (const p of a.prey) prey += cell.pop[p] * this.traitsOf(p, cell, t).yield;
      return Math.min(a.headAtBest, prey / PREDATOR_SUPPORT);
    }
    if (s === 'scavenger') {
      // scavengers are carried by death: fresh carrion, plus the unrest of a
      // freshly disturbed cell — a burn draws them before the bodies do
      return Math.min(a.headAtBest, cell.carrion * SCAV_PER_CARRION + h.instability * SCAV_INSTABILITY);
    }
    const w = a.habitatWeight;
    let forage = h.foragePotential, woody = h.woodyBiomass;
    if (!a.aquatic) {
      // the sere: each stage's share of the cell reads its own multiplier,
      // the climax remainder reads 1
      let f = 1, b = 1;
      for (const st of ['bare', 'grass', 'shrub', 'young'] as const) {
        f += shares[st] * (STAGE_FORAGE[st] - 1);
        b += shares[st] * (STAGE_BROWSE[st] - 1);
      }
      // and the season: the cure takes the forage where there is no water
      forage *= f * (1 - DRY_CUT * seasonalDryness(dayStart(t)) * (1 - h.waterAccess));
      woody *= b;
    }
    const q = clamp(w.forage * forage + w.woody * woody + w.water * h.waterAccess, 0, 1);
    return a.headAtBest * q * (a.aquatic ? 1 : h.landFraction);
  }

  /** One day for every stocked cell, at the boundary t = dayIndex · DAY. */
  private daily(t: number, dayIndex: number): void {
    for (const cell of this.cells.values()) {
      const h = this.habitat.at(cell.sx, cell.sz, t);
      const shares = this.sharesFor(cell, t);
      // yesterday's carrion rots before today's deaths land on the pile
      cell.carrion *= CARRION_KEEP;
      // …and yesterday's hand on the herds fades, each press on its own
      // half-life, before today's processes read what is left of it
      if (cell.press) {
        for (const s of WILDLIFE_SPECIES) {
          const by = cell.press[s];
          if (!by) continue;
          for (const k of PROCESSES) {
            const p = by[k];
            if (!p) continue;
            p.v *= Math.pow(0.5, 1 / Math.max(0.5, p.half));
            if (Math.abs(p.v) < PRESS_FLOOR) delete by[k];
          }
          if (Object.keys(by).length === 0) delete cell.press[s];
        }
        if (Object.keys(cell.press).length === 0) delete cell.press;
      }
      // what stood yesterday, for the vanish / return crossings
      const before = { ...cell.pop };

      // the range: unfed head strip it, time rebuilds it. Read against K0 —
      // the habitat's own K, before the range takes its share — so a herd
      // sitting at what its worn range carries is not overstocked and the
      // range can heal under it.
      let unfed = 0;
      for (const s of HERBIVORES) {
        const k0 = this.capacity(s, h, cell, shares, t);
        const most = WILDLIFE_ARCHETYPES[s].headAtBest * h.landFraction;
        if (most > 0 && cell.pop[s] > k0) unfed = Math.max(unfed, (cell.pop[s] - k0) / most);
      }
      cell.range = clamp(
        cell.range + RANGE_RECOVER * (1 - cell.range)
          - RANGE_STRIP * Math.max(0, Math.min(unfed, UNFED_CAP) - UNFED_FLOOR), 0, 1);
      const rangeFactor = RANGE_MIN + (1 - RANGE_MIN) * cell.range;

      for (const s of WILDLIFE_SPECIES) {
        const a = WILDLIFE_ARCHETYPES[s];
        cell.prevCap[s] = cell.cap[s];
        let k = this.capacity(s, h, cell, shares, t);
        if (HERBIVORES.includes(s)) k *= rangeFactor;
        cell.cap[s] = k;
        let pop = cell.pop[s];
        if (pop <= 0) { cell.pop[s] = 0; continue; }
        if (pop > k) {
          // the land no longer carries them: a share of the excess starves —
          // and feeds the scavengers. This is disturbance mortality: a burned
          // cell's K collapses and the herd dies down toward what remains —
          // slower for a hardy strain's herds (tolerance divides, per the
          // axis contract: how much performance survives when conditions turn)
          const dead = (pop - k) * EXCESS_MORTALITY / this.traitsOf(s, cell, t).tolerance;
          pop -= dead;
          cell.carrion += dead;
        } else if (k > 0) {
          // …and a hand on the herd's increase, while it is increasing: the
          // logistic rate reads the press (Beasts, *Green Years*), and reads
          // nothing at all for a herd at or over its K, which is what makes
          // this a lean on a process rather than a number
          pop += a.growthPerDay * pressFactor(cell.press?.[s]?.increase?.v ?? 0) * pop * (1 - pop / k);
        }
        cell.pop[s] = pop < EXTINCT ? 0 : pop;
      }

      // predation: each predator removes a fixed intake, spread over the prey
      // in proportion to their abundance, capped so no herd is stripped in a day
      const pred = cell.pop.predator;
      if (pred > 0) {
        const preyList = WILDLIFE_ARCHETYPES.predator.prey;
        // the split is weighted by each herd's EXPOSURE — how readily the
        // ambient destructive process takes it (the axis contract) — so a
        // wary strain's herd loses less of the pack's attention than its
        // abundance alone would earn it, and a careless one draws the wolves
        let prey = 0;
        for (const p of preyList) prey += cell.pop[p] * this.traitsOf(p, cell, t).exposure;
        if (prey > 0) {
          // …and a hand on the hunt (Beasts, *Hunt as One*): the pack's intake
          // reads the press on the predator cell, still capped per herd by
          // MAX_PREDATION — a leaned-on hunt takes more, never strips a herd
          const need = pred * PREDATOR_INTAKE * pressFactor(cell.press?.predator?.hunt?.v ?? 0);
          for (const p of preyList) {
            const w = cell.pop[p] * this.traitsOf(p, cell, t).exposure;
            const kill = Math.min(need * (w / prey), cell.pop[p] * MAX_PREDATION);
            cell.pop[p] -= kill;
            cell.carrion += kill * CARRION_PER_KILL;
          }
        }
      }

      // the two crossings the world can remember (law 16): a species dying
      // out of a cell it stood on, and one coming back after a real absence.
      // A hysteresis pair, so a cell hovering at the bar cannot chatter: gone
      // is falling under ONE head, back is standing at TWO (`RETURN_HEAD`,
      // the bar migration.ts uses for a herd to be a herd at all) — and a
      // return inside a year is a flicker, cleared silently.
      const centre = socialCenter(cell.sx, cell.sz);
      for (const s of WILDLIFE_SPECIES) {
        const was = before[s], now = cell.pop[s];
        if (cell.gone[s] === undefined) {
          if (was >= VANISH_HEAD && now < VANISH_HEAD) {
            cell.gone[s] = t;
            // Said only when the ground could still carry a herd of them: then
            // something took them — a hunt, a predator, a walk-out — and that
            // is a sentence with a story in it. A species dying out of a cell
            // whose K has gone under it (a burn's bare year, a stripped range)
            // is the K's story, already told by the burn; the ledger records
            // the absence either way, so the RETURN can be dated. Measured on
            // the shipped century (`tools/trajectory-migrations.ts 730`):
            // ungated, predators and scavengers "vanished" 135 and 147 times a
            // year from cells that carried under two head — chatter.
            if (cell.cap[s] >= RETURN_HEAD) {
              this.events.emit({
                type: VANISHED, time: t, x: centre.x, z: centre.z,
                radius: SOCIAL_CELL_SIZE, magnitude: was,
                salience: vanishSalience(s, was),
                cause: { kind: 'natural' },
                data: { species: s, head: was },
              });
            }
          }
        } else if (now >= RETURN_HEAD) {
          const away = t - cell.gone[s]!;
          delete cell.gone[s];
          if (away >= RETURN_AFTER) {
            this.events.emit({
              type: RETURNED, time: t, x: centre.x, z: centre.z,
              radius: SOCIAL_CELL_SIZE, magnitude: now,
              salience: returnSalience(s, away),
              cause: { kind: 'natural' },
              data: { species: s, head: now, absentDays: Math.round(away / DAY) },
            });
          }
        }
      }

      cell.updatedAt = t;
    }

    // then the herds that lost their footing walk out
    migrationPass(this.cells, dayIndex, this.seed, this.events);
  }
}

/**
 * How much a local extinction is worth saying. Sized against the kind's own
 * `greatMove` — the head count that makes a move of that kind great is also
 * the head count whose loss is. It only ever fires from ground that could
 * still carry them (see the emit), so the base is already the story's.
 */
const vanishSalience = (s: WildlifeSpeciesId, was: number): number => {
  const great = WILDLIFE_ARCHETYPES[s].greatMove;
  const size = clamp(was / great, 0, 1);
  return clamp(0.5 + 0.35 * size, 0, 0.85);
};

/** A return is worth more the longer they were gone — a year is news, a decade is history. */
const returnSalience = (s: WildlifeSpeciesId, awaySeconds: number): number => {
  const years = awaySeconds / (365 * DAY);
  const rare = s === 'grazer' || s === 'browser' || s === 'predator' ? 0.1 : 0;
  return clamp(0.5 + rare + 0.06 * Math.min(years, 5), 0, 0.9);
};
